import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Shield,
  Download,
  Trash2,
  Lock,
  Eye,
  EyeOff,
  FileText,
  AlertTriangle,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

export function PrivacyTab() {
  const { user, deleteAccount } = useAuth();
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Privacy preferences - stored in localStorage
  const [preferences, setPreferences] = useState(() => {
    const saved = localStorage.getItem('outlier_communication_preferences');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // Fall back to defaults if parsing fails
      }
    }
    return {
      marketingEmails: false,
      productUpdates: true,
      performanceReports: true,
      partnerSharing: false,
    };
  });

  const handleExportData = async () => {
    if (!user?.id) {
      toast.error('You must be logged in to export data');
      return;
    }

    setIsExporting(true);
    try {
      // Fetch all user data
      const userId = user.id;
      const [profileResult, holdingsResult, partnersResult] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', userId).single(),
        supabase.from('holdings').select('*').eq('user_id', userId),
        supabase.from('partner_links').select('*').or(`user_id.eq.${userId},partner_id.eq.${userId}`),
      ]);

      const exportData = {
        exportedAt: new Date().toISOString(),
        user: {
          id: user?.id,
          email: user?.email,
        },
        profile: profileResult.data,
        holdings: holdingsResult.data || [],
        partnerLinks: partnersResult.data || [],
      };

      // Create and download JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `outlier-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Your data has been exported successfully');
    } catch {
      toast.error('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setIsDeleting(true);
    try {
      const { error } = await deleteAccount();

      if (error) {
        throw error;
      }

      toast.success('Your account has been deleted');
      // User will be redirected to auth page by the auth state change
    } catch {
      toast.error('Failed to delete account. Please contact support.');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setDeleteConfirmation('');
    }
  };

  const handlePreferenceChange = (key: keyof typeof preferences, value: boolean) => {
    const updated = { ...preferences, [key]: value };
    setPreferences(updated);
    localStorage.setItem('outlier_communication_preferences', JSON.stringify(updated));
    toast.success('Preferences saved');
  };

  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold">Privacy & Security</h1>
        <p className="text-sm text-muted-foreground">
          Manage your data, privacy settings, and account security
        </p>
      </div>

      {/* Data Protection Overview */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            Your Data is Protected
          </CardTitle>
          <CardDescription>
            We take your privacy seriously. Here's how we protect your information.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="p-4 rounded-lg bg-muted/50 space-y-2">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-primary" />
                <h4 className="font-medium text-sm">Encryption</h4>
              </div>
              <p className="text-xs text-muted-foreground">
                All data is encrypted in transit (TLS 1.3) and at rest (AES-256)
              </p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 space-y-2">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-primary" />
                <h4 className="font-medium text-sm">Read-Only Access</h4>
              </div>
              <p className="text-xs text-muted-foreground">
                We only have read-only access to your linked accounts - we can never move your money
              </p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 space-y-2">
              <div className="flex items-center gap-2">
                <EyeOff className="h-4 w-4 text-primary" />
                <h4 className="font-medium text-sm">No Credential Storage</h4>
              </div>
              <p className="text-xs text-muted-foreground">
                Your bank/brokerage login credentials are never stored on our servers
              </p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 space-y-2">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                <h4 className="font-medium text-sm">SOC 2 Compliant</h4>
              </div>
              <p className="text-xs text-muted-foreground">
                Our infrastructure follows SOC 2 Type II security standards
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Communication Preferences */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Communication Preferences</CardTitle>
          <CardDescription>
            Choose what emails and notifications you receive
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="productUpdates" className="text-sm font-medium">
                Product Updates
              </Label>
              <p className="text-xs text-muted-foreground">
                New features and improvements to the platform
              </p>
            </div>
            <Switch
              id="productUpdates"
              checked={preferences.productUpdates}
              onCheckedChange={(v) => handlePreferenceChange('productUpdates', v)}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="performanceReports" className="text-sm font-medium">
                Performance Reports
              </Label>
              <p className="text-xs text-muted-foreground">
                Weekly or monthly portfolio performance summaries
              </p>
            </div>
            <Switch
              id="performanceReports"
              checked={preferences.performanceReports}
              onCheckedChange={(v) => handlePreferenceChange('performanceReports', v)}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="marketingEmails" className="text-sm font-medium">
                Marketing Emails
              </Label>
              <p className="text-xs text-muted-foreground">
                Tips, educational content, and promotional offers
              </p>
            </div>
            <Switch
              id="marketingEmails"
              checked={preferences.marketingEmails}
              onCheckedChange={(v) => handlePreferenceChange('marketingEmails', v)}
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="partnerSharing" className="text-sm font-medium">
                Partner Data Sharing
              </Label>
              <p className="text-xs text-muted-foreground">
                Share anonymized data with research partners
              </p>
            </div>
            <Switch
              id="partnerSharing"
              checked={preferences.partnerSharing}
              onCheckedChange={(v) => handlePreferenceChange('partnerSharing', v)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Export Data */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Download className="h-4 w-4 text-primary" />
            Export Your Data
          </CardTitle>
          <CardDescription>
            Download a copy of all your data in JSON format
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Your export will include your profile information, all holdings, linked accounts, and
            partner connections. This file is for your records and can be used to port your data.
          </p>
          <Button variant="outline" onClick={handleExportData} disabled={isExporting} className="gap-2">
            {isExporting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Preparing Export...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Download My Data
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Legal Documents */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Legal Documents</CardTitle>
          <CardDescription>Review our policies and terms</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Privacy Policy</span>
            </div>
            <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </a>
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Terms of Service</span>
            </div>
            <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </a>
          <a
            href="/security"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors group"
          >
            <div className="flex items-center gap-3">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Security Overview</span>
            </div>
            <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
          </a>
        </CardContent>
      </Card>

      {/* Delete Account - Danger Zone */}
      <Card className="border-destructive/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-4 w-4" />
            Danger Zone
          </CardTitle>
          <CardDescription>
            Irreversible actions that affect your account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-lg bg-destructive/5 border border-destructive/20 space-y-3">
            <div className="flex items-start gap-3">
              <Trash2 className="h-5 w-5 text-destructive mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-sm">Delete Account</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Permanently delete your account and all associated data. This action cannot be
                  undone. All your holdings, linked accounts, and partner connections will be
                  removed.
                </p>
              </div>
            </div>

            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="border-destructive/30 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete My Account
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                    <AlertTriangle className="h-5 w-5" />
                    Are you absolutely sure?
                  </AlertDialogTitle>
                  <AlertDialogDescription className="space-y-3">
                    <p>
                      This action <strong>cannot be undone</strong>. This will permanently delete
                      your account and remove all associated data from our servers.
                    </p>
                    <ul className="text-sm space-y-1 list-disc list-inside">
                      <li>Your profile and settings</li>
                      <li>All portfolio holdings and history</li>
                      <li>Linked brokerage accounts</li>
                      <li>Partner connections</li>
                    </ul>
                    <div className="pt-3">
                      <Label htmlFor="delete-confirm" className="text-sm font-medium">
                        Type <strong>DELETE</strong> to confirm
                      </Label>
                      <Input
                        id="delete-confirm"
                        value={deleteConfirmation}
                        onChange={(e) => setDeleteConfirmation(e.target.value)}
                        placeholder="DELETE"
                        className="mt-2"
                      />
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteAccount}
                    disabled={isDeleting || deleteConfirmation !== 'DELETE'}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {isDeleting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      'Delete Account'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {/* Help Contact */}
      <div className="text-center text-sm text-muted-foreground">
        <p>
          Have questions about your privacy?{' '}
          <a href="mailto:privacy@outlier.app" className="text-primary hover:underline">
            Contact our privacy team
          </a>
        </p>
      </div>
    </div>
  );
}
